import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDate } from '../utils/date';
import { curLabel } from '../utils/currency';
import { useT } from '../utils/translate';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const fmt = n => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

function OrderRow({ order, t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-sm">
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between gap-3 text-left">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-ink">№{order.number}</span>
            {order.customStatus && (
              <span className="badge-gray text-[10px]" style={order.customStatus.color ? { backgroundColor: `${order.customStatus.color}20`, color: order.customStatus.color } : undefined}>
                {order.customStatus.name}
              </span>
            )}
          </div>
          <p className="text-xs text-ink-tertiary mt-1">{formatDate(order.date)}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <p className="text-sm font-bold text-ink">{fmt(order.totalAmount)} {curLabel(order.currency)}</p>
          {open ? <ChevronUp className="w-4 h-4 text-ink-tertiary" /> : <ChevronDown className="w-4 h-4 text-ink-tertiary" />}
        </div>
      </button>
      {open && (
        <div className="mt-2 pt-2 border-t border-surface-100 space-y-1.5">
          {(order.items || []).map((it, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-ink-secondary truncate pr-2">{it.name} × {fmt(it.qty)} {it.uom}</span>
              <span className="text-ink font-medium shrink-0">{fmt(it.amount)} {curLabel(order.currency)}</span>
            </div>
          ))}
          {order.note && <p className="text-xs text-ink-tertiary pt-1">{order.note}</p>}
        </div>
      )}
    </div>
  );
}

// Mijozning o'zi client-app'dan yuborgan sotuv buyurtmalari ro'yxati.
export default function OrdersPage() {
  const t = useT();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/client/orders`).then(res => setOrders(res.data.orders || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-7 h-7 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
        <Package className="w-10 h-10 text-ink-tertiary mb-3" />
        <p className="text-sm text-ink-tertiary">{t('orders.empty')}</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2">
      <h1 className="text-lg font-bold text-ink mb-1">{t('orders.title')}</h1>
      {orders.map(o => <OrderRow key={o._id} order={o} t={t} />)}
    </div>
  );
}
